import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { getLesson, LESSONS, lessonsByCategory } from "@/content/lessons";
import { getCategory } from "@/content/categories";
import { completeLesson } from "@/lib/progress";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import { toast } from "sonner";

export default function LessonDetail() {
  const { t, lp, code, param } = useLang();
  const { slug } = useParams();
  const lesson = getLesson(slug);
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState(null);

  const steps = useMemo(() => {
    if (!lesson) return [];
    const arr = [{ type: "objectives" }];
    lesson.sections.forEach((s, i) => arr.push({ type: "section", i }));
    arr.push({ type: "check" });
    arr.push({ type: "complete" });
    return arr;
  }, [lesson]);

  if (!lesson) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="text-lg font-semibold text-[#475569]">{t("lesson.notFound")}</p>
        <Link to={lp("/learn")} className="mt-4 inline-block font-bold text-[#147DEB] hover:underline">← {t("nav.learn")}</Link>
      </div>
    );
  }

  const cat = getCategory(lesson.category);
  const cur = steps[step];
  const pct = Math.round(((step + 1) / steps.length) * 100);
  const idx = LESSONS.findIndex((l) => l.slug === slug);
  const nextLesson = LESSONS[idx + 1] || LESSONS[0];
  const related = lessonsByCategory(lesson.category).filter((l) => l.slug !== slug).slice(0, 3);

  const goNext = () => {
    if (cur.type === "complete") return;
    if (steps[step + 1]?.type === "complete") {
      completeLesson(slug);
      toast.success(t("lesson.completedTitle"));
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <>
      <Seo title={lesson.title[code]} description={lesson.desc[code]} />

      {/* top bar */}
      <div className="border-b border-[#147DEB]/10 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <Link to={lp("/learn")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline" data-testid="lesson-back-link">
              <Icon name="ArrowLeft" className="h-4 w-4" /> {t("nav.learn")}
            </Link>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ backgroundColor: `${cat.color}1a`, color: cat.color }}>
              <Icon name={cat.icon} className="h-3.5 w-3.5" /> {cat.name[code]}
            </span>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-semibold text-[#64748B]">
              <span>{t("lesson.progress")}</span>
              <span data-testid="lesson-progress-pct">{pct}%</span>
            </div>
            <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-[#E8F5FF]">
              <div className="h-full rounded-full bg-gradient-to-r from-[#147DEB] to-[#45C878] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#082B66] sm:text-4xl">{lesson.title[code]}</h1>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            {cur.type === "objectives" && (
              <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7 card-shadow-soft">
                <h2 className="font-display flex items-center gap-2 text-xl font-bold text-[#082B66]">
                  <Icon name="Target" className="h-6 w-6 text-[#147DEB]" /> {t("lesson.objectives")}
                </h2>
                <ul className="mt-5 space-y-3">
                  {lesson.objectives[code].map((o, i) => (
                    <li key={i} className="flex items-start gap-3 text-base text-[#334155]">
                      <Icon name="CheckCircle2" className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#45C878]" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {cur.type === "section" && (
              <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7 card-shadow-soft">
                <span className="text-xs font-bold uppercase tracking-wider text-[#147DEB]">
                  {t("lesson.section")} {cur.i + 1} / {lesson.sections.length}
                </span>
                <h2 className="font-display mt-2 text-2xl font-bold text-[#082B66]">{lesson.sections[cur.i].title[code]}</h2>
                <p className="mt-4 text-lg leading-relaxed text-[#334155]">{lesson.sections[cur.i].body[code]}</p>
              </div>
            )}

            {cur.type === "check" && (
              <div className="rounded-3xl border-2 border-[#FFC928]/40 bg-gradient-to-br from-[#FFF9E6] to-white p-7">
                <h2 className="font-display flex items-center gap-2 text-xl font-bold text-[#082B66]">
                  <Icon name="HelpCircle" className="h-6 w-6 text-[#FFC928]" /> {t("lesson.check")}
                </h2>
                <p className="mt-4 text-base font-semibold text-[#475569]">{t("lesson.checkPrompt")}</p>
                <p className="mt-2 text-lg font-bold text-[#082B66]">{lesson.check.q[code]}</p>
                <div className="mt-5 space-y-3">
                  {lesson.check.options[code].map((opt, i) => {
                    const isCorrect = i === lesson.check.answer;
                    const show = picked !== null;
                    return (
                      <button
                        key={i}
                        onClick={() => setPicked(i)}
                        data-testid={`lesson-check-option-${i}`}
                        className={`flex w-full items-center justify-between rounded-2xl border-2 px-5 py-4 text-left text-base font-semibold transition-all ${
                          show && isCorrect
                            ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                            : show && picked === i
                            ? "border-rose-300 bg-rose-50 text-rose-700"
                            : "border-[#147DEB]/15 bg-white text-[#082B66] hover:border-[#147DEB]/40"
                        }`}
                      >
                        {opt}
                        {show && isCorrect && <Icon name="CheckCircle2" className="h-5 w-5 text-emerald-500" />}
                        {show && !isCorrect && picked === i && <Icon name="XCircle" className="h-5 w-5 text-rose-400" />}
                      </button>
                    );
                  })}
                </div>
                {picked !== null && (
                  <p className={`mt-4 font-bold ${picked === lesson.check.answer ? "text-emerald-600" : "text-rose-500"}`} data-testid="lesson-check-feedback">
                    {picked === lesson.check.answer ? t("common.correct") : t("common.incorrect")}
                  </p>
                )}
              </div>
            )}

            {cur.type === "complete" && (
              <div className="rounded-3xl border-2 border-[#45C878]/30 bg-gradient-to-br from-[#EAFBF1] to-white p-10 text-center" data-testid="lesson-complete-screen">
                <span className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-white ring-4 ring-[#45C878]/30">
                  <img src="/assets/lumora-logo.jpeg" alt="LUMORA mascots cheering" className="h-full w-full object-contain" />
                </span>
                <h2 className="font-display mt-5 text-3xl font-extrabold text-[#082B66]">{t("lesson.completedTitle")}</h2>
                <p className="mt-2 text-base text-[#475569]">{t("lesson.completedDesc")}</p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <Link to={lp(`/learn/${nextLesson.slug}`)} onClick={() => { setStep(0); setPicked(null); }} className="inline-flex items-center gap-2 rounded-2xl bg-[#147DEB] px-6 py-3 font-bold text-white hover:bg-[#0F62BD]" data-testid="lesson-next-lesson-cta">
                    {t("lesson.next")}: {nextLesson.title[code]} <Icon name="ArrowRight" className="h-4 w-4" />
                  </Link>
                  <Link to={lp("/learn")} className="inline-flex items-center gap-2 rounded-2xl bg-[#E8F5FF] px-6 py-3 font-bold text-[#147DEB]">{t("nav.learn")}</Link>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav buttons */}
        {cur.type !== "complete" && (
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              onClick={goPrev}
              disabled={step === 0}
              data-testid="lesson-prev-button"
              className="inline-flex items-center gap-1.5 rounded-2xl border-2 border-[#147DEB]/15 bg-white px-5 py-3 text-sm font-bold text-[#082B66] transition-colors hover:border-[#147DEB] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Icon name="ArrowLeft" className="h-4 w-4" /> {t("common.previous")}
            </button>
            <button
              onClick={goNext}
              disabled={cur.type === "check" && picked === null}
              data-testid="lesson-next-button"
              className="inline-flex items-center gap-1.5 rounded-2xl bg-[#147DEB] px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#0F62BD] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {steps[step + 1]?.type === "complete" ? t("lesson.markComplete") : t("common.next")}
              <Icon name="ArrowRight" className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* related */}
        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="font-display text-xl font-bold text-[#082B66]">{t("lesson.related")}</h3>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to={lp(`/learn/${r.slug}`)} onClick={() => { setStep(0); setPicked(null); }} className="rounded-2xl border-2 border-[#147DEB]/10 bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`related-lesson-${r.slug}`}>
                  <h4 className="font-display font-bold text-[#082B66]">{r.title[code]}</h4>
                  <p className="mt-1 text-sm text-[#64748B]">{r.minutes} {t("common.min")}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
